import { Box, Group, Pagination, Select, SimpleGrid, Text } from "@mantine/core"
import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import barClasses from "../ui/bulk-action-bar.module.css"
import { StatusPill, type StatusTone } from "../ui/status-pill"
import classes from "./atoms.module.css"

/** Status tone: a dot colour only. Text stays dark slate; no coloured borders or stripes (UI standard). */
export type Tone = "red" | "amber" | "green" | "blue" | "grey"

export function Dot({ tone }: Readonly<{ tone: Tone }>) {
  return <Box component="span" className={classes.dot} data-tone={tone} aria-hidden />
}

const PILL_TONE: Record<Tone, StatusTone> = { red: "red", amber: "yellow", green: "green", blue: "blue", grey: "gray" }

/** Status pill: the shared ui/StatusPill (same look as de-network-admin «● Зөвшөөрсөн»). */
export function DotPill({ tone, children }: Readonly<{ tone: Tone; children: ReactNode }>) {
  return <StatusPill tone={PILL_TONE[tone] ?? "gray"}>{children}</StatusPill>
}

export type KpiItem = {
  key: string
  label: ReactNode
  value: ReactNode
  meta?: ReactNode
  /** Optional leading dot; omit for a neutral tile. */
  tone?: Tone
  /** Small suffix after the value, e.g. «/ 3». */
  suffix?: ReactNode
}

/** White bordered KPI tile: dot + label, mono value, one-line meta (canvas KPI row). */
export function KpiTile({ label, value, meta, tone, suffix }: Readonly<Omit<KpiItem, "key">>) {
  return (
    <Box className={classes.tile}>
      <Group gap={8} wrap="nowrap">
        {tone && <Dot tone={tone} />}
        <Text className={classes.tileLabel}>{label}</Text>
      </Group>
      <Text className={classes.tileValue}>
        {value}
        {suffix && (
          <Text span className={classes.tileSuffix}>
            {suffix}
          </Text>
        )}
      </Text>
      {meta != null && <Text className={classes.tileMeta}>{meta}</Text>}
    </Box>
  )
}

export function KpiRow({ items, cols }: Readonly<{ items: KpiItem[]; cols?: number }>) {
  return (
    <SimpleGrid cols={{ base: 2, md: cols ?? items.length }} spacing="md">
      {items.map(({ key, ...item }) => (
        <KpiTile key={key} {...item} />
      ))}
    </SimpleGrid>
  )
}

export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]

/**
 * One footer for every Хүргэлт удирдлага tab (inside TableFrame): «Харагдаж буй: 1 - 20 / 103»
 * on the left, pages + page size on the right. Pages show only when there is more than one;
 * the size picker stays while the total exceeds the smallest size, so a 100-row page can go back.
 */
export function TablePager({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
}: Readonly<{
  page: number
  pageSize: number
  total: number
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
}>) {
  const { t } = useTranslation("delivery")
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)
  const showSize = !!onPageSizeChange && total > PAGE_SIZE_OPTIONS[0]
  return (
    <>
      <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
        {t("manage.pending.showing", { from, to, total })}
      </Text>
      {(pageCount > 1 || showSize) && (
        <Group gap="sm">
          {pageCount > 1 && <Pagination value={Math.min(page, pageCount)} onChange={onPageChange} total={pageCount} size="sm" />}
          {showSize && (
            <Select
              w={130}
              data={PAGE_SIZE_OPTIONS.map((v) => ({ value: String(v), label: t("manage.pending.perPage", { n: v }) }))}
              value={String(pageSize)}
              onChange={(v) => {
                if (v) {
                  onPageSizeChange(Number(v))
                  onPageChange(1)
                }
              }}
              allowDeselect={false}
            />
          )}
        </Group>
      )}
    </>
  )
}

/** The footer strip of a TableFrame, for tables that render their own footer (CustomTable). */
export function FrameFooter({ children }: Readonly<{ children: ReactNode }>) {
  return <Box className={classes.frameFooter}>{children}</Box>
}

/** Bordered white frame around a table (radius lg, soft shadow). */
export function TableFrame({ children, footer }: Readonly<{ children: ReactNode; footer?: ReactNode }>) {
  return (
    <Box className={classes.frame}>
      {children}
      {footer && <Box className={classes.frameFooter}>{footer}</Box>}
    </Box>
  )
}

/**
 * Bar while rows are selected: «N хүргэлт сонгосон» + actions. Same floating dark bar as
 * ui/BulkActionBar (de-invoice-admin pattern); callers keep passing their own buttons.
 */
export function SelectionBar({ label, children }: Readonly<{ label: ReactNode; children: ReactNode }>) {
  return (
    <Box className={barClasses.bar} role="toolbar">
      <Text className={barClasses.text}>{label}</Text>
      <Box className={barClasses.divider} aria-hidden />
      <Box className={barClasses.actions}>{children}</Box>
    </Box>
  )
}

/** Two-line cell: mono code on top, secondary code below (DN / SO). */
export function CodeStack({ primary, secondary }: Readonly<{ primary: ReactNode; secondary?: ReactNode }>) {
  return (
    <Box>
      <Text ff="monospace" fw={600} fz="var(--nw-fz-mono)" translate="no">
        {primary ?? "-"}
      </Text>
      {secondary && (
        <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)" translate="no">
          {secondary}
        </Text>
      )}
    </Box>
  )
}
